<script setup>
/* global axios */
// Página "Gerenciar Funis" (rota própria, NÃO modal). Criar/editar abrem uma
// PÁGINA interna (view='form'), não um pop-up. Listing com busca + filtros.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import draggable from 'vuedraggable';
import KanbanSelect from './KanbanSelect.vue';
import KanbanMultiSelect from './KanbanMultiSelect.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/kanban`;

const COLORS = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#84CC16',
  '#F97316',
  '#6366F1',
];
const funnels = ref([]);
const sequenceFlows = ref([]);
const loading = ref(true);
// 'list' = listagem | 'form' = página de criar/editar
const view = ref('list');
const formMode = ref('create'); // 'create' | 'edit'
const editId = ref(null);
// funil sendo editado (p/ Templates de Itens + Campos personalizados no wizard)
const editingFunnel = computed(
  () => funnels.value.find(x => x.id === editId.value) || null
);
// Detalhes do funil (compartilhados por criar e editar)
const nName = ref('');
const nColor = ref('#3B82F6');
// Visibilidade: 'private' (só admin) | 'public' (todos) | 'restricted' (usuários escolhidos)
const visibility = ref('private');
const allowedUserIds = ref([]);
const agents = ref([]);
const agentOptions = computed(() =>
  agents.value.map(a => ({
    value: a.id,
    label: a.name || a.email || `#${a.id}`,
  }))
);
// Busca + filtros do listing
const searchQuery = ref('');
const filterStatus = ref('all'); // all | active | inactive
const filterVisibility = ref('all'); // all | public | private
const expanded = ref(new Set());
const stageAddFunnel = ref(null);
const stageAddName = ref('');
const editStageId = ref(null);
const esName = ref('');
const esColor = ref('');

const load = async () => {
  funnels.value = (await axios.get(`${base}/funnels`)).data.data || [];
  loading.value = false;
};

// ---- Busca + filtros do listing ----
const statusOptions = computed(() => [
  { value: 'all', label: t('KANBAN.funnelManagement.filterStatusAll') },
  { value: 'active', label: t('KANBAN.funnelManagement.active') },
  { value: 'inactive', label: t('KANBAN.funnelManagement.inactive') },
]);
const visibilityOptions = computed(() => [
  { value: 'all', label: t('KANBAN.funnelManagement.filterVisAll') },
  { value: 'public', label: t('KANBAN.funnelManagement.publicBadge') },
  { value: 'private', label: t('KANBAN.privateBadge') },
]);
const hasFilters = computed(
  () =>
    searchQuery.value.trim() !== '' ||
    filterStatus.value !== 'all' ||
    filterVisibility.value !== 'all'
);
const clearFilters = () => {
  searchQuery.value = '';
  filterStatus.value = 'all';
  filterVisibility.value = 'all';
};
const filteredFunnels = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  return funnels.value.filter(f => {
    if (q && !(f.name || '').toLowerCase().includes(q)) return false;
    if (filterStatus.value === 'active' && !f.is_active) return false;
    if (filterStatus.value === 'inactive' && f.is_active) return false;
    if (filterVisibility.value === 'public' && !f.is_public) return false;
    if (filterVisibility.value === 'private' && f.is_public) return false;
    return true;
  });
});
const loadSequences = async () => {
  try {
    const fs = (await axios.get(`${base}/flows`)).data.data || [];
    sequenceFlows.value = fs.filter(
      f => f.flow_type === 'sequence' && f.is_active
    );
  } catch (e) {
    sequenceFlows.value = [];
  }
};
const loadAgents = async () => {
  try {
    agents.value =
      (await axios.get(`/api/v1/accounts/${accountId}/agents`)).data || [];
  } catch (e) {
    agents.value = [];
  }
};
// Mapeia o modo de visibilidade da UI para o payload do backend.
const visibilityPayload = () => ({
  is_public: visibility.value === 'public',
  allowed_user_ids:
    visibility.value === 'restricted' ? allowedUserIds.value : [],
});
const visibilityFromFunnel = f => {
  if (f.is_public) return 'public';
  if ((f.allowed_user_ids || []).length) return 'restricted';
  return 'private';
};

// ---- Wizard de criação de funil (passo a passo) ----
const wizardStep = ref(1);
const creating = ref(false);
const wStages = ref([]);
// automações por etapa no wizard (clicar na etapa abre/fecha o painel)
const defaultAutomations = () => ({
  new_ticket: false,
  transfer_funnel_id: null,
  auto_message: false,
  sequence_flow_id: null,
});
const wStageOpen = ref(null);
const toggleWStage = i => {
  wStageOpen.value = wStageOpen.value === i ? null : i;
};
// Na EDIÇÃO há 3 passos (o 2º = Templates de Itens + Campos personalizados);
// na criação, só Detalhes/Etapas + Revisão.
const wizardSteps = computed(() => {
  const steps = [t('KANBAN.funnelWizard.stepDetailsStages')];
  if (formMode.value === 'edit')
    steps.push(t('KANBAN.funnelWizard.stepTemplatesFields'));
  steps.push(t('KANBAN.funnelWizard.stepReview'));
  return steps;
});
const lastStep = computed(() => wizardSteps.value.length);
const defaultStageSet = () => [
  { name: t('KANBAN.funnelWizard.presetNewLead'), color: '#6B7280', automations: defaultAutomations() },
  { name: t('KANBAN.funnelWizard.presetContacted'), color: '#F59E0B', automations: defaultAutomations() },
  { name: t('KANBAN.funnelWizard.presetNegotiation'), color: '#3B82F6', automations: defaultAutomations() },
  { name: t('KANBAN.funnelWizard.presetClosed'), color: '#10B981', automations: defaultAutomations() },
];
const validStages = computed(() =>
  wStages.value.map(s => ({ ...s, name: s.name.trim() })).filter(s => s.name)
);
const canNext = computed(() => {
  // Passo 1 = Detalhes + Etapas (duas colunas): exige nome E ao menos 1 etapa.
  if (wizardStep.value === 1)
    return nName.value.trim().length > 0 && validStages.value.length > 0;
  return true;
});
const openNew = () => {
  formMode.value = 'create';
  editId.value = null;
  view.value = 'form';
  wizardStep.value = 1;
  creating.value = false;
  nName.value = '';
  nColor.value = '#3B82F6';
  visibility.value = 'private';
  allowedUserIds.value = [];
  wStages.value = defaultStageSet();
};
const nextStep = () => {
  if (wizardStep.value < lastStep.value && canNext.value) wizardStep.value += 1;
};
const prevStep = () => {
  if (wizardStep.value > 1) wizardStep.value -= 1;
};
const addWizardStage = () => {
  wStages.value.push({
    name: '',
    color: COLORS[wStages.value.length % COLORS.length],
    automations: defaultAutomations(),
  });
};
const removeWizardStage = i => {
  wStages.value.splice(i, 1);
  if (wStageOpen.value === i) wStageOpen.value = null;
};
const cycleStageColor = i => {
  const cur = COLORS.indexOf(wStages.value[i].color);
  wStages.value[i].color = COLORS[(cur + 1) % COLORS.length];
};
const createFunnel = async () => {
  const name = nName.value.trim();
  if (!name || creating.value) return;
  creating.value = true;
  try {
    await axios.post(`${base}/funnels`, {
      name,
      color: nColor.value,
      ...visibilityPayload(),
      stages: validStages.value.map(s => ({ name: s.name, color: s.color, automations: s.automations || {} })),
    });
    view.value = 'list';
    await load();
  } finally {
    creating.value = false;
  }
};
const editOrigStageIds = ref([]);
const startEdit = f => {
  formMode.value = 'edit';
  editId.value = f.id;
  creating.value = false;
  wizardStep.value = 1;
  nName.value = f.name;
  nColor.value = f.color;
  visibility.value = visibilityFromFunnel(f);
  allowedUserIds.value = [...(f.allowed_user_ids || [])];
  // etapas no wizard (com id, p/ diff no salvar)
  wStages.value = (f.stages || []).map(s => ({
    id: s.id,
    name: s.name,
    color: s.color,
    automations: { ...defaultAutomations(), ...(s.automations || {}) },
  }));
  wStageOpen.value = null;
  editOrigStageIds.value = (f.stages || []).map(s => s.id);
  view.value = 'form';
};
// submit unificado: cria ou edita (edição usa o mesmo wizard)
const submitFunnel = () =>
  formMode.value === 'edit' ? saveEdit() : createFunnel();
const saveEdit = async () => {
  const name = nName.value.trim();
  if (!name || creating.value) return;
  creating.value = true;
  try {
    await axios.patch(`${base}/funnels/${editId.value}`, {
      name,
      color: nColor.value,
      ...visibilityPayload(),
    });
    // sincroniza etapas: cria novas, atualiza existentes, remove as retiradas
    const keptIds = [];
    for (let i = 0; i < validStages.value.length; i += 1) {
      const s = validStages.value[i];
      if (s.id) {
        keptIds.push(s.id);
        // eslint-disable-next-line no-await-in-loop
        await axios.patch(`${base}/stages/${s.id}`, {
          name: s.name,
          color: s.color,
          position: i,
          automations: s.automations || {},
        });
      } else {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await axios.post(
          `${base}/funnels/${editId.value}/stages`,
          { name: s.name, color: s.color, position: i, automations: s.automations || {} }
        );
        const newId = data?.data?.id;
        if (newId) keptIds.push(newId);
      }
    }
    await Promise.all(
      editOrigStageIds.value
        .filter(id => !keptIds.includes(id))
        .map(id => axios.delete(`${base}/stages/${id}`).catch(() => {}))
    );
    editId.value = null;
    view.value = 'list';
    await load();
  } finally {
    creating.value = false;
  }
};
const closeForm = () => {
  view.value = 'list';
  editId.value = null;
};
const removeFunnel = async f => {
  if (!window.confirm(t('KANBAN.prompts.deleteFunnelCards', { name: f.name })))
    return;
  await axios.delete(`${base}/funnels/${f.id}`);
  await load();
};
const toggleActive = async f => {
  await axios.patch(`${base}/funnels/${f.id}`, { is_active: !f.is_active });
  await load();
};
const duplicate = async f => {
  await axios.post(`${base}/funnels/${f.id}/duplicate`);
  await load();
};
const toggleExpand = id => {
  const s = new Set(expanded.value);
  if (s.has(id)) s.delete(id);
  else s.add(id);
  expanded.value = s;
};
const deleteStage = async s => {
  if (!window.confirm(t('KANBAN.prompts.deleteStage', { name: s.name })))
    return;
  await axios.delete(`${base}/stages/${s.id}`);
  await load();
};
const addStage = async f => {
  const n = stageAddName.value.trim();
  if (!n) return;
  await axios.post(`${base}/funnels/${f.id}/stages`, { name: n });
  stageAddName.value = '';
  stageAddFunnel.value = null;
  await load();
};
const startEditStage = s => {
  editStageId.value = s.id;
  esName.value = s.name;
  esColor.value = s.color;
};
const saveStageEdit = async s => {
  await axios.patch(`${base}/stages/${s.id}`, {
    name: esName.value,
    color: esColor.value,
  });
  editStageId.value = null;
  await load();
};
const onStageReorder = async f => {
  // Sequential PATCH by index — backend reorders by position, order matters.
  for (let i = 0; i < f.stages.length; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    await axios.patch(`${base}/stages/${f.stages[i].id}`, { position: i });
  }
};
const saveAutomations = async s => {
  await axios.patch(`${base}/stages/${s.id}`, {
    automations: s.automations || {},
  });
};
// Templates de Itens
const fmtMoney = c =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    (c || 0) / 100
  );
const reaisToCents = v =>
  Math.round(
    (parseFloat(String(v).replace(/\./g, '').replace(',', '.')) || 0) * 100
  );
const expandedTpl = ref(new Set());
const tplAddFunnel = ref(null);
const tplTitle = ref('');
const tplValue = ref('0,00');
const toggleTpl = id => {
  const s = new Set(expandedTpl.value);
  if (s.has(id)) s.delete(id);
  else s.add(id);
  expandedTpl.value = s;
};
const addTemplate = async f => {
  const title = tplTitle.value.trim();
  if (!title) return;
  await axios.post(`${base}/funnels/${f.id}/item_templates`, {
    title,
    value: reaisToCents(tplValue.value),
  });
  tplTitle.value = '';
  tplValue.value = '0,00';
  tplAddFunnel.value = null;
  await load();
};
const removeTemplate = async (f, tpl) => {
  await axios.delete(`${base}/funnels/${f.id}/item_templates/${tpl.id}`);
  await load();
};
// Campos Personalizados
const CF_TYPES = [
  ['text', 'cfTypes.text'],
  ['number', 'cfTypes.number'],
  ['date', 'cfTypes.date'],
  ['select', 'cfTypes.select'],
  ['multi_select', 'cfTypes.multiSelect'],
  ['checkbox', 'cfTypes.checkbox'],
  ['url', 'cfTypes.url'],
  ['email', 'cfTypes.email'],
  ['phone', 'cfTypes.phone'],
];
const cfTypeLabel = type => {
  const k = (CF_TYPES.find(x => x[0] === type) || [type, type])[1];
  return k.startsWith('cfTypes.') ? t('KANBAN.' + k) : k;
};
const expandedCf = ref(new Set());
const cfAddFunnel = ref(null);
const cfName = ref('');
const cfType = ref('text');
const cfOptions = ref('');
const toggleCf = id => {
  const s = new Set(expandedCf.value);
  if (s.has(id)) s.delete(id);
  else s.add(id);
  expandedCf.value = s;
};
const addCustomField = async f => {
  const n = cfName.value.trim();
  if (!n) return;
  const opts = ['select', 'multi_select'].includes(cfType.value)
    ? cfOptions.value
        .split(',')
        .map(o => o.trim())
        .filter(Boolean)
    : [];
  await axios.post(`${base}/funnels/${f.id}/custom_fields`, {
    name: n,
    field_type: cfType.value,
    options: opts,
  });
  cfName.value = '';
  cfOptions.value = '';
  cfType.value = 'text';
  cfAddFunnel.value = null;
  await load();
};
const removeCustomField = async (f, cf) => {
  await axios.delete(`${base}/funnels/${f.id}/custom_fields/${cf.id}`);
  await load();
};
const goBack = () => router.push(`/app/accounts/${accountId}/kanban`);

onMounted(() => {
  load();
  loadSequences();
  loadAgents();
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-y-auto bg-n-background">
    <!-- ===================== LISTA ===================== -->
    <template v-if="view === 'list'">
      <!-- Header da página -->
      <div
        class="sticky top-0 z-10 bg-n-background border-b border-n-weak dark:bg-n-solid-2"
      >
        <div class="flex items-center h-16 gap-4 px-6">
          <button
            class="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-lg text-n-slate-11 hover:bg-n-alpha-1"
            @click="goBack"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            {{ t('KANBAN.funnelManagement.back') }}
          </button>
          <h1 class="text-heading-1 text-n-slate-12">
            {{ t('KANBAN.funnelManagement.manageFunnels') }}
          </h1>
          <button
            class="ml-auto inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
            @click="openNew"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 4v16m8-8H4"
              />
            </svg>
            {{ t('KANBAN.funnelManagement.createNewFunnel') }}
          </button>
        </div>
      </div>

      <!-- Conteúdo -->
      <main class="flex-1 p-6 overflow-auto">
        <div v-if="loading" class="flex justify-center py-12 text-n-slate-11">
          {{ t('KANBAN.loading') }}
        </div>
        <div
          v-else
          class="overflow-hidden bg-n-background border rounded-lg shadow-sm border-n-weak"
        >
          <!-- toolbar: busca + filtros (igual Flows) -->
          <div
            class="flex flex-wrap items-center gap-2 px-4 py-2 border-b border-n-weak"
          >
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="t('KANBAN.funnelManagement.searchPlaceholder')"
              class="reset-base flex-1 min-w-[12rem] px-3 py-2 text-sm rounded-lg outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-slate-6 focus:outline-n-blue-9 bg-n-alpha-1 text-n-slate-12"
            />
            <div class="w-44 shrink-0">
              <KanbanSelect v-model="filterStatus" :options="statusOptions" />
            </div>
            <div class="w-52 shrink-0">
              <KanbanSelect
                v-model="filterVisibility"
                :options="visibilityOptions"
              />
            </div>
          </div>
          <table class="min-w-full divide-y divide-n-weak">
            <thead class="bg-n-alpha-1">
              <tr>
                <th
                  class="px-4 py-2 text-xs font-medium tracking-wider text-left uppercase text-n-slate-10"
                >
                  {{ t('KANBAN.funnelManagement.thFunnel') }}
                </th>
                <th
                  class="px-4 py-2 text-xs font-medium tracking-wider text-center uppercase text-n-slate-10"
                >
                  {{ t('KANBAN.funnelManagement.thStages') }}
                </th>
                <th
                  class="px-4 py-2 text-xs font-medium tracking-wider text-center uppercase text-n-slate-10"
                >
                  {{ t('KANBAN.funnelManagement.thVisibility') }}
                </th>
                <th
                  class="px-4 py-2 text-xs font-medium tracking-wider text-center uppercase text-n-slate-10"
                >
                  {{ t('KANBAN.funnelManagement.thStatus') }}
                </th>
                <th
                  class="px-4 py-2 text-xs font-medium tracking-wider text-right uppercase text-n-slate-10"
                >
                  {{ t('KANBAN.funnelManagement.thActions') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr v-if="!filteredFunnels.length">
                <td
                  colspan="5"
                  class="px-4 py-12 text-sm text-center text-n-slate-11"
                >
                  {{ t('KANBAN.funnelManagement.noResults') }}
                </td>
              </tr>
              <tr
                v-for="f in filteredFunnels"
                :key="f.id"
                class="hover:bg-n-alpha-1"
              >
                <td class="px-4 py-3">
                  <div class="flex items-center gap-2">
                    <span
                      class="w-3.5 h-3.5 rounded-full shrink-0"
                      :style="{ backgroundColor: f.color }"
                    />
                    <span class="font-medium text-n-slate-12">{{ f.name }}</span>
                    <span
                      v-if="f.is_system"
                      class="px-2 py-0.5 text-[0.625rem] font-medium rounded-full bg-n-iris-3 text-n-iris-11"
                      >{{ t('KANBAN.funnelManagement.systemBadge') }}</span
                    >
                  </div>
                </td>
                <td class="px-4 py-3 text-sm text-center text-n-slate-11">
                  {{ f.stages.length }}
                </td>
                <td class="px-4 py-3 text-center">
                  <span
                    class="px-2 py-0.5 text-[0.625rem] font-medium rounded-full"
                    :class="
                      f.is_public
                        ? 'bg-n-teal-3 text-n-teal-11'
                        : (f.allowed_user_ids || []).length
                          ? 'bg-n-iris-3 text-n-iris-11'
                          : 'bg-n-alpha-2 text-n-slate-11'
                    "
                    >{{
                      f.is_public
                        ? t('KANBAN.funnelManagement.publicBadge')
                        : (f.allowed_user_ids || []).length
                          ? t('KANBAN.funnelManagement.usersSelected', {
                              count: f.allowed_user_ids.length,
                            })
                          : t('KANBAN.privateBadge')
                    }}</span
                  >
                </td>
                <td class="px-4 py-3">
                  <div class="flex justify-center">
                    <button
                      class="relative w-9 h-5 rounded-full transition-colors"
                      :class="f.is_active ? 'bg-n-teal-9' : 'bg-n-slate-5'"
                      :title="
                        f.is_active
                          ? t('KANBAN.funnelManagement.active')
                          : t('KANBAN.funnelManagement.inactive')
                      "
                      @click="toggleActive(f)"
                    >
                      <span
                        class="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all"
                        :class="f.is_active ? 'left-[18px]' : 'left-0.5'"
                      />
                    </button>
                  </div>
                </td>
                <td class="px-4 py-3">
                  <div class="flex items-center justify-end gap-1.5">
                    <button
                      class="inline-flex items-center justify-center rounded-lg w-9 h-9 text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12"
                      :title="t('KANBAN.actions.edit')"
                      @click="startEdit(f)"
                    >
                      <svg
                        class="w-[18px] h-[18px]"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path
                          d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"
                        />
                      </svg>
                    </button>
                    <button
                      v-if="!f.is_system"
                      class="inline-flex items-center justify-center rounded-lg w-9 h-9 text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12"
                      :title="t('KANBAN.actions.duplicate')"
                      @click="duplicate(f)"
                    >
                      <svg
                        class="w-[18px] h-[18px]"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <rect x="9" y="9" width="11" height="11" rx="2" />
                        <path
                          d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
                        />
                      </svg>
                    </button>
                    <button
                      v-if="!f.is_system"
                      class="inline-flex items-center justify-center rounded-lg w-9 h-9 text-n-ruby-11 hover:bg-n-ruby-3"
                      :title="t('KANBAN.actions.delete')"
                      @click="removeFunnel(f)"
                    >
                      <svg
                        class="w-[18px] h-[18px]"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path
                          d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"
                        />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </main>
    </template>

    <!-- ===================== FORM (criar/editar) ===================== -->
    <template v-else>
      <!-- Header da página -->
      <div
        class="sticky top-0 z-10 bg-n-background border-b border-n-weak dark:bg-n-solid-2"
      >
        <div class="flex items-center h-16 gap-4 px-6">
          <button
            class="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-lg text-n-slate-11 hover:bg-n-alpha-1"
            @click="closeForm"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            {{ t('KANBAN.funnelManagement.back') }}
          </button>
          <h1 class="text-heading-1 text-n-slate-12">
            {{
              formMode === 'edit'
                ? t('KANBAN.funnelManagement.editFunnelTitle')
                : t('KANBAN.funnelWizard.title')
            }}
          </h1>
        </div>
      </div>

      <!-- CRIAR/EDITAR: wizard passo a passo (edição usa o mesmo formato) -->
      <div class="flex flex-col w-full max-w-3xl px-6 pt-4 pb-8 mx-auto">
        <!-- stepper -->
        <div class="flex items-center px-8 pt-5 pb-1 shrink-0">
          <template v-for="(label, i) in wizardSteps" :key="i">
            <div class="flex flex-col items-center gap-1.5">
              <div
                class="grid text-sm font-semibold transition-all rounded-full w-9 h-9 place-items-center"
                :class="
                  wizardStep > i + 1
                    ? 'bg-n-brand text-white'
                    : wizardStep === i + 1
                      ? 'bg-n-brand text-white ring-4 ring-n-brand/20'
                      : 'bg-n-alpha-2 text-n-slate-10'
                "
              >
                <svg
                  v-if="wizardStep > i + 1"
                  class="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M20 6 9 17l-5-5"
                  />
                </svg>
                <span v-else>{{ i + 1 }}</span>
              </div>
              <span
                class="text-xs whitespace-nowrap"
                :class="
                  wizardStep === i + 1
                    ? 'text-n-slate-12 font-medium'
                    : 'text-n-slate-10'
                "
                >{{ label }}</span
              >
            </div>
            <div
              v-if="i < wizardSteps.length - 1"
              class="flex-1 h-0.5 mx-2 mb-5 rounded transition-colors"
              :class="wizardStep > i + 1 ? 'bg-n-brand' : 'bg-n-alpha-2'"
            />
          </template>
        </div>

        <!-- corpo -->
        <div class="px-1 py-5">
          <!-- Passo 1: Detalhes (coluna 1) + Etapas (coluna 2) -->
          <div
            v-if="wizardStep === 1"
            class="grid grid-cols-1 gap-6 md:grid-cols-2"
          >
            <!-- Coluna 1: Detalhes -->
            <div class="space-y-5">
              <div>
                <label
                  class="block mb-1.5 text-sm font-medium text-n-slate-12"
                  >{{ t('KANBAN.funnelWizard.nameLabel') }}</label
                >
                <input
                  v-model="nName"
                  :placeholder="
                    t('KANBAN.funnelManagement.funnelNamePlaceholder')
                  "
                  class="reset-base w-full px-3 py-2 text-sm rounded-lg outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-slate-6 focus:outline-n-blue-9 bg-n-alpha-1 text-n-slate-12"
                  @keyup.enter="nextStep"
                />
              </div>
              <div>
                <label class="block mb-2 text-sm font-medium text-n-slate-12">{{
                  t('KANBAN.funnelManagement.colorLabel')
                }}</label>
                <div class="flex flex-wrap gap-2">
                  <button
                    v-for="c in COLORS"
                    :key="c"
                    class="relative grid rounded-full w-7 h-7 place-items-center transition-transform hover:scale-110"
                    :style="{ backgroundColor: c }"
                    @click="nColor = c"
                  >
                    <svg
                      v-if="nColor === c"
                      class="w-4 h-4 text-white"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="3"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M20 6 9 17l-5-5"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <div>
                <label class="block mb-2 text-sm font-medium text-n-slate-12">{{
                  t('KANBAN.funnelWizard.visibilityLabel')
                }}</label>
                <div class="grid grid-cols-1 gap-2">
                  <button
                    type="button"
                    class="flex items-start gap-2.5 p-3 text-left transition-all rounded-lg outline -outline-offset-1"
                    :class="
                      visibility === 'private'
                        ? 'outline-2 outline-n-brand bg-n-alpha-1'
                        : 'outline-1 outline-n-weak hover:outline-n-slate-6'
                    "
                    @click="visibility = 'private'"
                  >
                    <svg
                      class="w-5 h-5 mt-0.5 shrink-0 text-n-slate-11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    <span class="min-w-0">
                      <span class="block text-sm font-medium text-n-slate-12">{{
                        t('KANBAN.funnelWizard.privateLabel')
                      }}</span>
                      <span class="block mt-0.5 text-xs text-n-slate-11">{{
                        t('KANBAN.funnelWizard.privateHint')
                      }}</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    class="flex items-start gap-2.5 p-3 text-left transition-all rounded-lg outline -outline-offset-1"
                    :class="
                      visibility === 'public'
                        ? 'outline-2 outline-n-brand bg-n-alpha-1'
                        : 'outline-1 outline-n-weak hover:outline-n-slate-6'
                    "
                    @click="visibility = 'public'"
                  >
                    <svg
                      class="w-5 h-5 mt-0.5 shrink-0 text-n-slate-11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path
                        d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"
                      />
                    </svg>
                    <span class="min-w-0">
                      <span class="block text-sm font-medium text-n-slate-12">{{
                        t('KANBAN.funnelWizard.publicLabel')
                      }}</span>
                      <span class="block mt-0.5 text-xs text-n-slate-11">{{
                        t('KANBAN.funnelWizard.publicHint')
                      }}</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    class="flex items-start gap-2.5 p-3 text-left transition-all rounded-lg outline -outline-offset-1"
                    :class="
                      visibility === 'restricted'
                        ? 'outline-2 outline-n-brand bg-n-alpha-1'
                        : 'outline-1 outline-n-weak hover:outline-n-slate-6'
                    "
                    @click="visibility = 'restricted'"
                  >
                    <svg
                      class="w-5 h-5 mt-0.5 shrink-0 text-n-slate-11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path
                        d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
                      />
                    </svg>
                    <span class="min-w-0">
                      <span class="block text-sm font-medium text-n-slate-12">{{
                        t('KANBAN.funnelWizard.restrictedLabel')
                      }}</span>
                      <span class="block mt-0.5 text-xs text-n-slate-11">{{
                        t('KANBAN.funnelWizard.restrictedHint')
                      }}</span>
                    </span>
                  </button>
                </div>
                <!-- multi-select de usuários (visibilidade Específico) -->
                <div v-if="visibility === 'restricted'" class="mt-3">
                  <label
                    class="block mb-1.5 text-xs font-medium text-n-slate-11"
                    >{{ t('KANBAN.funnelManagement.selectUsersLabel') }}</label
                  >
                  <KanbanMultiSelect
                    v-model="allowedUserIds"
                    :options="agentOptions"
                    :placeholder="t('KANBAN.funnelManagement.selectUsersLabel')"
                  />
                </div>
              </div>
            </div>

            <!-- Coluna 2: Etapas -->
            <div class="space-y-3">
              <label class="block text-sm font-medium text-n-slate-12">{{
                t('KANBAN.funnelWizard.stepStages')
              }}</label>
              <p class="text-xs text-n-slate-11">
                {{ t('KANBAN.funnelWizard.stagesHint') }}
              </p>
              <draggable
                :list="wStages"
                item-key="__idx"
                handle=".stage-drag"
                class="space-y-2"
              >
                <template #item="{ element, index }">
                  <div
                    class="rounded-lg outline outline-1 -outline-offset-1 bg-n-alpha-1"
                    :class="wStageOpen === index ? 'outline-n-brand' : 'outline-n-weak'"
                  >
                    <div class="flex items-center gap-2 p-2">
                      <span
                        class="stage-drag cursor-grab text-n-slate-9 shrink-0"
                        :title="t('KANBAN.funnelWizard.dragStage')"
                      >
                        <svg
                          class="w-4 h-4"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <circle cx="9" cy="6" r="1.5" />
                          <circle cx="15" cy="6" r="1.5" />
                          <circle cx="9" cy="12" r="1.5" />
                          <circle cx="15" cy="12" r="1.5" />
                          <circle cx="9" cy="18" r="1.5" />
                          <circle cx="15" cy="18" r="1.5" />
                        </svg>
                      </span>
                      <button
                        class="w-5 h-5 rounded-full shrink-0 ring-2 ring-offset-1 ring-offset-n-background ring-transparent hover:ring-n-strong"
                        :style="{ backgroundColor: element.color }"
                        :title="t('KANBAN.funnelWizard.changeColor')"
                        @click="cycleStageColor(index)"
                      />
                      <input
                        v-model="element.name"
                        :placeholder="
                          t('KANBAN.funnelWizard.stageNamePlaceholder')
                        "
                        class="reset-base flex-1 min-w-0 px-2.5 py-1.5 text-sm rounded-lg outline outline-1 -outline-offset-1 outline-n-weak focus:outline-n-blue-9 bg-n-background text-n-slate-12"
                      />
                      <span
                        class="text-xs tabular-nums text-n-slate-9 shrink-0 w-5 text-center"
                        >{{ index + 1 }}</span
                      >
                      <!-- abre/fecha as configurações (automações) desta etapa -->
                      <button
                        class="inline-flex items-center justify-center rounded-md w-7 h-7 shrink-0 hover:bg-n-alpha-2"
                        :class="
                          wStageOpen === index
                            ? 'text-n-brand'
                            : 'text-n-slate-10 hover:text-n-slate-12'
                        "
                        :title="t('KANBAN.funnelWizard.stageSettings')"
                        @click="toggleWStage(index)"
                      >
                        <svg
                          class="w-4 h-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                        >
                          <circle cx="12" cy="12" r="3" />
                          <path
                            d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
                          />
                        </svg>
                      </button>
                      <button
                        class="inline-flex items-center justify-center rounded-md w-7 h-7 shrink-0 text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-alpha-2 disabled:opacity-40 disabled:cursor-not-allowed"
                        :disabled="wStages.length <= 1"
                        :title="t('KANBAN.funnelWizard.removeStage')"
                        @click="removeWizardStage(index)"
                      >
                        <svg
                          class="w-4 h-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"
                          />
                        </svg>
                      </button>
                    </div>
                    <!-- painel de automações da etapa (abre ao clicar na engrenagem) -->
                    <div
                      v-if="wStageOpen === index"
                      class="flex flex-col gap-2 px-3 pt-1 pb-3 mx-2 mb-2 border-t border-dashed border-n-weak"
                    >
                      <label
                        class="flex items-center gap-1.5 text-xs text-n-slate-11 pt-1"
                      >
                        <input v-model="element.automations.new_ticket" type="checkbox" />
                        {{ t('KANBAN.funnelManagement.newTicketsFallHere') }}
                      </label>
                      <div class="flex items-center gap-1.5 text-xs text-n-slate-11">
                        <span class="text-n-slate-10 shrink-0">{{
                          t('KANBAN.funnelManagement.transferOnArrival')
                        }}</span>
                        <select
                          v-model="element.automations.transfer_funnel_id"
                          class="flex-1 min-w-0 pl-2 pr-8 py-1 text-xs rounded-md border border-n-weak bg-n-background text-n-slate-11"
                        >
                          <option :value="null">
                            {{ t('KANBAN.funnelManagement.selectFunnel') }}
                          </option>
                          <option v-for="ff in funnels" :key="ff.id" :value="ff.id">
                            {{ ff.name }}
                          </option>
                        </select>
                      </div>
                      <label
                        class="flex items-center gap-1.5 text-xs text-n-slate-11"
                      >
                        <input v-model="element.automations.auto_message" type="checkbox" />
                        {{ t('KANBAN.funnelManagement.sendAutoMessage') }}
                      </label>
                      <div class="flex items-center gap-1.5 text-xs text-n-slate-11">
                        <span class="text-n-slate-10 shrink-0">{{
                          t('KANBAN.funnelManagement.flowSequenceOnEnter')
                        }}</span>
                        <select
                          v-model="element.automations.sequence_flow_id"
                          class="flex-1 min-w-0 pl-2 pr-8 py-1 text-xs rounded-md border border-n-weak bg-n-background text-n-slate-11"
                        >
                          <option :value="null">
                            {{ t('KANBAN.funnelManagement.noSequence') }}
                          </option>
                          <option v-for="sq in sequenceFlows" :key="sq.id" :value="sq.id">
                            {{ sq.name }}
                          </option>
                        </select>
                      </div>
                      <div class="flex items-center gap-1.5 text-xs text-n-slate-11">
                        <span class="text-n-slate-10 shrink-0">{{
                          t('KANBAN.funnelManagement.emailOnEnterStage')
                        }}</span>
                        <select
                          class="flex-1 min-w-0 pl-2 pr-8 py-1 text-xs rounded-md border border-n-weak bg-n-background text-n-slate-10 disabled:bg-n-alpha-1"
                          disabled
                          :title="t('KANBAN.requiresEmailModule')"
                        >
                          <option>{{ t('KANBAN.funnelManagement.noTemplate') }}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </template>
              </draggable>
              <button
                class="flex items-center justify-center w-full gap-1.5 px-3 py-2 text-sm font-medium border border-dashed rounded-lg border-n-weak text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12"
                @click="addWizardStage"
              >
                <svg
                  class="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path stroke-linecap="round" d="M12 5v14M5 12h14" />
                </svg>
                {{ t('KANBAN.funnelWizard.addStage') }}
              </button>
            </div>
          </div>

          <!-- Passo 2 (edição): Templates de Itens + Campos personalizados -->
          <div
            v-if="wizardStep === 2 && formMode === 'edit' && editingFunnel"
            class="max-w-2xl mx-auto space-y-4"
          >
            <div class="space-y-1">
              <h2 class="text-lg font-semibold text-n-slate-12">
                {{ t('KANBAN.funnelWizard.stepTemplatesFields') }}
              </h2>
              <p class="text-sm text-n-slate-11">
                {{ t('KANBAN.funnelWizard.templatesFieldsHint') }}
              </p>
            </div>
            <!-- Templates de Itens -->
            <div class="border border-n-weak rounded-lg overflow-hidden">
              <button
                class="flex items-center gap-1.5 w-full px-3 py-2 text-sm font-medium text-n-iris-11 bg-n-iris-3"
                @click="toggleTpl(editingFunnel.id)"
              >
                <span class="inline-flex items-center gap-1.5">
                  <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20.59 13.41 12 22l-9-9V3h10l7.59 7.59a2 2 0 0 1 0 2.82z" />
                    <circle cx="7.5" cy="7.5" r="1.5" fill="currentColor" />
                  </svg>
                  {{ t('KANBAN.funnelManagement.itemTemplates') }}
                </span>
                <span class="px-[7px] text-[0.6875rem] rounded-full bg-n-iris-4 text-n-iris-11">{{ editingFunnel.item_templates?.length || 0 }}</span>
                <span class="ml-auto">{{ expandedTpl.has(editingFunnel.id) ? '▾' : '▸' }}</span>
              </button>
              <div
                v-if="expandedTpl.has(editingFunnel.id)"
                class="flex flex-col gap-1 px-3 py-2 bg-n-background dark:bg-n-solid-2"
              >
                <div
                  v-for="tpl in editingFunnel.item_templates || []"
                  :key="tpl.id"
                  class="flex items-center gap-2 py-1"
                >
                  <span class="text-sm text-n-slate-12">{{ tpl.title }}</span>
                  <span class="ml-auto text-sm font-medium text-n-teal-11">{{ fmtMoney(tpl.value) }}</span>
                  <button
                    class="inline-flex items-center justify-center px-1.5 text-n-ruby-11 hover:brightness-90"
                    @click="removeTemplate(editingFunnel, tpl)"
                  >
                    <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                    </svg>
                  </button>
                </div>
                <div v-if="!(editingFunnel.item_templates || []).length" class="py-2 text-xs text-center text-n-slate-10">
                  {{ t('KANBAN.funnelManagement.noTemplateCreated') }}
                </div>
                <div v-if="tplAddFunnel === editingFunnel.id" class="flex gap-1 mt-1">
                  <input
                    v-model="tplTitle"
                    :placeholder="t('KANBAN.itemNamePlaceholder')"
                    class="grow px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
                    @keyup.enter="addTemplate(editingFunnel)"
                  />
                  <input
                    v-model="tplValue"
                    :placeholder="t('KANBAN.funnelManagement.valuePlaceholder')"
                    class="w-24 px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
                  />
                  <button
                    class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
                    @click="addTemplate(editingFunnel)"
                  >
                    +
                  </button>
                </div>
                <button
                  v-else
                  class="mt-1 px-2 py-1.5 text-xs rounded-md border border-dashed border-n-weak text-n-slate-10 bg-n-alpha-1 hover:bg-n-alpha-2"
                  @click="
                    tplAddFunnel = editingFunnel.id;
                    tplTitle = '';
                    tplValue = '0,00';
                  "
                >
                  {{ t('KANBAN.addTemplateBtn') }}
                </button>
              </div>
            </div>

            <!-- Campos Personalizados -->
            <div class="border border-n-weak rounded-lg overflow-hidden">
              <button
                class="flex items-center gap-1.5 w-full px-3 py-2 text-sm font-medium text-n-iris-11 bg-n-iris-3"
                @click="toggleCf(editingFunnel.id)"
              >
                <span class="inline-flex items-center gap-1.5">
                  <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="8" y="2" width="8" height="4" rx="1" />
                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                  </svg>
                  {{ t('KANBAN.funnelManagement.customFields') }}
                </span>
                <span class="px-[7px] text-[0.6875rem] rounded-full bg-n-iris-4 text-n-iris-11">{{ editingFunnel.custom_fields?.length || 0 }}</span>
                <span class="ml-auto">{{ expandedCf.has(editingFunnel.id) ? '▾' : '▸' }}</span>
              </button>
              <div
                v-if="expandedCf.has(editingFunnel.id)"
                class="flex flex-col gap-1 px-3 py-2 bg-n-background dark:bg-n-solid-2"
              >
                <div
                  v-for="cf in editingFunnel.custom_fields || []"
                  :key="cf.id"
                  class="flex items-center gap-2 py-1"
                >
                  <span class="text-sm text-n-slate-12">{{ cf.name }}</span>
                  <span class="ml-auto px-2 py-0.5 text-[0.625rem] rounded bg-n-alpha-1 text-n-slate-10 dark:bg-n-solid-3 dark:text-n-slate-11">{{ cfTypeLabel(cf.field_type) }}</span>
                  <button
                    class="inline-flex items-center justify-center px-1.5 text-n-ruby-11 hover:brightness-90"
                    @click="removeCustomField(editingFunnel, cf)"
                  >
                    <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                    </svg>
                  </button>
                </div>
                <div v-if="!(editingFunnel.custom_fields || []).length" class="py-2 text-xs text-center text-n-slate-10">
                  {{ t('KANBAN.noFieldCreated') }}
                </div>
                <div v-if="cfAddFunnel === editingFunnel.id" class="flex flex-col gap-1 mt-1">
                  <input
                    v-model="cfName"
                    :placeholder="t('KANBAN.fieldNamePlaceholder')"
                    class="px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
                    @keyup.enter="addCustomField(editingFunnel)"
                  />
                  <select
                    v-model="cfType"
                    class="pl-2 pr-8.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
                  >
                    <option v-for="ct in CF_TYPES" :key="ct[0]" :value="ct[0]">
                      {{ cfTypeLabel(ct[0]) }}
                    </option>
                  </select>
                  <input
                    v-if="['select', 'multi_select'].includes(cfType)"
                    v-model="cfOptions"
                    :placeholder="t('KANBAN.optionsCommaPlaceholder')"
                    class="px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
                  />
                  <div class="flex gap-1">
                    <button
                      class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
                      @click="addCustomField(editingFunnel)"
                    >
                      {{ t('KANBAN.funnelManagement.add') }}
                    </button>
                    <button
                      class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
                      @click="cfAddFunnel = null"
                    >
                      {{ t('KANBAN.funnelManagement.cancel') }}
                    </button>
                  </div>
                </div>
                <button
                  v-else
                  class="mt-1 px-2 py-1.5 text-xs rounded-md border border-dashed border-n-weak text-n-slate-10 bg-n-alpha-1 hover:bg-n-alpha-2"
                  @click="
                    cfAddFunnel = editingFunnel.id;
                    cfName = '';
                    cfType = 'text';
                    cfOptions = '';
                  "
                >
                  {{ t('KANBAN.addFieldBtn') }}
                </button>
              </div>
            </div>
          </div>

          <!-- Revisão (último passo) -->
          <div v-if="wizardStep === lastStep" class="space-y-4">
            <p class="text-xs text-n-slate-11">
              {{ t('KANBAN.funnelWizard.reviewHint') }}
            </p>
            <div
              class="overflow-hidden rounded-xl outline outline-1 -outline-offset-1 outline-n-weak"
            >
              <div
                class="flex items-center gap-3 px-4 py-3 border-b border-n-weak"
                :style="{
                  background: `linear-gradient(90deg, ${nColor}1A, transparent)`,
                }"
              >
                <span
                  class="w-3 h-3 rounded-full shrink-0"
                  :style="{ backgroundColor: nColor }"
                />
                <span class="text-sm font-semibold truncate text-n-slate-12">{{
                  nName || t('KANBAN.funnelWizard.untitled')
                }}</span>
                <span
                  class="ml-auto px-2 py-0.5 text-[0.6875rem] font-medium rounded-full shrink-0"
                  :class="
                    visibility === 'public'
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : visibility === 'restricted'
                        ? 'bg-n-iris-3 text-n-iris-11'
                        : 'bg-n-alpha-2 text-n-slate-11'
                  "
                  >{{
                    visibility === 'public'
                      ? t('KANBAN.funnelWizard.publicLabel')
                      : visibility === 'restricted'
                        ? t('KANBAN.funnelManagement.usersSelected', {
                            count: allowedUserIds.length,
                          })
                        : t('KANBAN.funnelWizard.privateLabel')
                  }}</span
                >
              </div>
              <div class="flex gap-2 p-3 overflow-x-auto">
                <div
                  v-for="(s, i) in validStages"
                  :key="i"
                  class="w-32 shrink-0 rounded-lg bg-n-alpha-1 border-t-2 px-2.5 py-2"
                  :style="{ borderTopColor: s.color }"
                >
                  <div class="text-xs font-medium truncate text-n-slate-12">
                    {{ s.name }}
                  </div>
                  <div class="mt-1 text-[0.6875rem] text-n-slate-10">
                    {{ t('KANBAN.funnelWizard.noCards') }}
                  </div>
                </div>
              </div>
            </div>
            <p class="text-xs text-center text-n-slate-10">
              {{
                t('KANBAN.funnelWizard.reviewSummary', {
                  count: validStages.length,
                })
              }}
            </p>
          </div>
        </div>

        <!-- rodapé -->
        <div
          class="flex items-center gap-3 px-1 pt-4 mt-2 border-t border-n-weak"
        >
          <button
            v-if="wizardStep > 1"
            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
            @click="prevStep"
          >
            <svg
              class="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path stroke-linecap="round" d="M15 18l-6-6 6-6" />
            </svg>
            {{ t('KANBAN.funnelWizard.back') }}
          </button>
          <button
            v-else
            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
            @click="closeForm"
          >
            {{ t('KANBAN.funnelManagement.cancel') }}
          </button>
          <span class="flex-1" />
          <button
            v-if="wizardStep < lastStep"
            class="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="!canNext"
            @click="nextStep"
          >
            {{ t('KANBAN.funnelWizard.next') }}
            <svg
              class="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path stroke-linecap="round" d="M9 18l6-6-6-6" />
            </svg>
          </button>
          <button
            v-else
            class="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="creating || !canNext"
            @click="submitFunnel"
          >
            <svg
              class="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M20 6 9 17l-5-5"
              />
            </svg>
            {{
              creating
                ? t('KANBAN.funnelWizard.creating')
                : formMode === 'edit'
                  ? t('KANBAN.funnelManagement.saveChanges')
                  : t('KANBAN.funnelWizard.create')
            }}
          </button>
        </div>
      </div>

      <!-- EDITAR (legado): substituído pelo wizard acima -->
      <div
        v-if="false"
        class="flex flex-col w-full max-w-2xl gap-5 px-6 pt-6 pb-8 mx-auto"
      >
        <div>
          <label class="block mb-1.5 text-sm font-medium text-n-slate-12">{{
            t('KANBAN.funnelWizard.nameLabel')
          }}</label>
          <input
            v-model="nName"
            :placeholder="t('KANBAN.funnelManagement.funnelNamePlaceholder')"
            class="reset-base w-full px-3 py-2 text-sm rounded-lg outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-slate-6 focus:outline-n-blue-9 bg-n-alpha-1 text-n-slate-12"
            @keyup.enter="saveEdit"
          />
        </div>
        <div>
          <label class="block mb-2 text-sm font-medium text-n-slate-12">{{
            t('KANBAN.funnelManagement.colorLabel')
          }}</label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="c in COLORS"
              :key="c"
              class="relative grid rounded-full w-7 h-7 place-items-center transition-transform hover:scale-110"
              :style="{ backgroundColor: c }"
              @click="nColor = c"
            >
              <svg
                v-if="nColor === c"
                class="w-4 h-4 text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="3"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M20 6 9 17l-5-5"
                />
              </svg>
            </button>
          </div>
        </div>
        <div>
          <label class="block mb-2 text-sm font-medium text-n-slate-12">{{
            t('KANBAN.funnelWizard.visibilityLabel')
          }}</label>
          <div class="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <button
              type="button"
              class="flex items-start gap-2.5 p-3 text-left transition-all rounded-lg outline -outline-offset-1"
              :class="
                visibility === 'private'
                  ? 'outline-2 outline-n-brand bg-n-alpha-1'
                  : 'outline-1 outline-n-weak hover:outline-n-slate-6'
              "
              @click="visibility = 'private'"
            >
              <svg
                class="w-5 h-5 mt-0.5 shrink-0 text-n-slate-11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span class="min-w-0">
                <span class="block text-sm font-medium text-n-slate-12">{{
                  t('KANBAN.funnelWizard.privateLabel')
                }}</span>
                <span class="block mt-0.5 text-xs text-n-slate-11">{{
                  t('KANBAN.funnelWizard.privateHint')
                }}</span>
              </span>
            </button>
            <button
              type="button"
              class="flex items-start gap-2.5 p-3 text-left transition-all rounded-lg outline -outline-offset-1"
              :class="
                visibility === 'public'
                  ? 'outline-2 outline-n-brand bg-n-alpha-1'
                  : 'outline-1 outline-n-weak hover:outline-n-slate-6'
              "
              @click="visibility = 'public'"
            >
              <svg
                class="w-5 h-5 mt-0.5 shrink-0 text-n-slate-11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="12" cy="12" r="10" />
                <path
                  d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"
                />
              </svg>
              <span class="min-w-0">
                <span class="block text-sm font-medium text-n-slate-12">{{
                  t('KANBAN.funnelWizard.publicLabel')
                }}</span>
                <span class="block mt-0.5 text-xs text-n-slate-11">{{
                  t('KANBAN.funnelWizard.publicHint')
                }}</span>
              </span>
            </button>
            <button
              type="button"
              class="flex items-start gap-2.5 p-3 text-left transition-all rounded-lg outline -outline-offset-1"
              :class="
                visibility === 'restricted'
                  ? 'outline-2 outline-n-brand bg-n-alpha-1'
                  : 'outline-1 outline-n-weak hover:outline-n-slate-6'
              "
              @click="visibility = 'restricted'"
            >
              <svg
                class="w-5 h-5 mt-0.5 shrink-0 text-n-slate-11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <span class="min-w-0">
                <span class="block text-sm font-medium text-n-slate-12">{{
                  t('KANBAN.funnelWizard.restrictedLabel')
                }}</span>
                <span class="block mt-0.5 text-xs text-n-slate-11">{{
                  t('KANBAN.funnelWizard.restrictedHint')
                }}</span>
              </span>
            </button>
          </div>
          <!-- multi-select de usuários (visibilidade Específico) -->
          <div v-if="visibility === 'restricted'" class="mt-3 max-w-sm">
            <label class="block mb-1.5 text-xs font-medium text-n-slate-11">{{
              t('KANBAN.funnelManagement.selectUsersLabel')
            }}</label>
            <KanbanMultiSelect
              v-model="allowedUserIds"
              :options="agentOptions"
              :placeholder="t('KANBAN.funnelManagement.selectUsersLabel')"
            />
          </div>
        </div>
        <p
          class="flex items-start gap-2 p-3 text-xs rounded-lg text-n-slate-11 bg-n-alpha-1"
        >
          <svg
            class="w-4 h-4 mt-px shrink-0 text-n-slate-10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="12" cy="12" r="10" />
            <path stroke-linecap="round" d="M12 16v-4M12 8h.01" />
          </svg>
          {{ t('KANBAN.funnelManagement.editStagesHint') }}
        </p>
        <div class="flex items-center gap-3 pt-4 mt-1 border-t border-n-weak">
          <button
            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
            @click="closeForm"
          >
            {{ t('KANBAN.funnelManagement.cancel') }}
          </button>
          <span class="flex-1" />
          <button
            class="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="creating || !nName.trim()"
            @click="saveEdit"
          >
            {{ t('KANBAN.funnelManagement.save') }}
          </button>
        </div>
      </div>
    </template>
  </div>
</template>
